import React, { useState, useEffect, useCallback,useContext } from 'react';
import { Table, Button, Spinner, Alert, Form, Row, Col, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import useAuth from '../context/useAuth';
import { DepositContext } from '../context/DepositContext';

const DepositTicketsList = () => {
    const { depositData, setDepositData } = useContext(DepositContext);

    const [formData, setFormData] = useState({
        start_range: depositData?.startRange || new Date(new Date().setDate(new Date().getDate() - 31)).toISOString().split('T')[0],
        end_range: depositData?.endRange || new Date().toISOString().split('T')[0],
    });

    const [depositTickets, setDepositTickets] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: depositData?.currentPage || 1,
        totalPages: 1,
    });

    const [loading, setLoading] = useState(false);
    const { resultsPerPage } = useAuth();
    const ticketsPerPage = parseInt(resultsPerPage, 10);
    const [errorMessage, setErrorMessage] = useState('');

    

    const fetchDepositTickets = useCallback(
        async (page = 1) => {
            try {
                setLoading(true);
                const { start_range, end_range } = formData;
                if (!start_range || !end_range) {
                    setErrorMessage('Both Start Date and End Date are required.');
                    setLoading(false);
                    return;
                }

                if (new Date(end_range) < new Date(start_range)) {
                    setErrorMessage('End Date cannot be before Start Date.');
                    setLoading(false);
                    return;
                }

                const { data } = await api.get('/api/depositTickets', {
                    params: {
                        page,
                        limit: ticketsPerPage,
                        start_date: start_range,
                        end_date: end_range,
                    },
                });
                setDepositTickets(data.records);
                setPagination({ currentPage: data.currentPage, totalPages: data.totalPages });
                setErrorMessage('');
                setDepositData({
                    currentPage: data.currentPage,
                    startRange: start_range,
                    endRange: end_range,
                }); // Persist to context
            } catch (error) {
                setErrorMessage(
                    error.response?.data?.error || 'Failed to fetch deposit tickets list. Please try again later.'
                );
            } finally {
                setLoading(false);
            }
        },
        [formData, ticketsPerPage,setDepositData]
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchDepositTickets();
    };

    const handlePageChange = (page) => {
        fetchDepositTickets(page);
    };

    useEffect(() => {
        fetchDepositTickets(pagination.currentPage);
    }, [fetchDepositTickets,pagination.currentPage]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${month}/${day}/${year}`;
    };
    const handleTicketClick = () => {
        setDepositData({
            currentPage: pagination.currentPage,
            startRange: formData.start_range,
            endRange: formData.end_range,
        });
    };

    return (
        <Container className="mt-5">
            <h2 className="text-center mb-4">Deposit Tickets</h2>
            {errorMessage && (
                <Alert variant="danger" className="text-center">
                    {errorMessage}
                </Alert>
            )}
            <Form onSubmit={handleSearch} className="mb-4">
                <Row className="g-3">
                    <Col md={6}>
                        <Form.Group controlId="start_range">
                            <Form.Label className="small-font">Start Range:</Form.Label>
                            <Form.Control
                                type="date"
                                name="start_range"
                                value={formData.start_range}
                                onChange={handleChange}
                                className="small-font"
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="end_range">
                            <Form.Label className="small-font">End Range:</Form.Label>
                            <Form.Control
                                type="date"
                                name="end_range"
                                value={formData.end_range}
                                onChange={handleChange}
                                className="small-font"
                                required
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <div className="mt-3">
                    <Button type="submit" variant="primary" className="px-5 small-font">
                        Search
                    </Button>
                </div>
            </Form>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table striped bordered hover size="sm" className="small-font">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Deposit Ticket Date</th>
                                <th>Range</th>
                                <th>Deno 100</th>
                                <th>Deno 50</th>
                                <th>Deno 20</th>
                                <th>Deno 10</th>
                                <th>Deno 5</th>
                                <th>Deno 2</th>
                                <th>Deno 1</th>
                                <th>Currency Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {depositTickets.length > 0 ? (
                                depositTickets.map((ticket) => {
                                    const formatDeno = (denomination, count) =>
                                        count
                                            ? `${denomination} x ${count} = $${(denomination * count).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                                            : '0';
                                    const currencyTotal =
                                        (ticket.deno_100 || 0) * 100 +
                                        (ticket.deno_50 || 0) * 50 +
                                        (ticket.deno_20 || 0) * 20 +
                                        (ticket.deno_10 || 0) * 10 +
                                        (ticket.deno_5 || 0) * 5 +
                                        (ticket.deno_2 || 0) * 2 +
                                        (ticket.deno_1 || 0) * 1;

                                    return (
                                        <tr key={ticket.id}>
                                            <td>
                                                <Link
                                                    to={`/viewDepositTicket/${ticket.id}`}
                                                    onClick={() => handleTicketClick()}
                                                    className="receipt-link"
                                                >
                                                    {ticket.id}
                                                </Link>
                                            </td>
                                            <td>{formatDate(ticket.deptkt_date)}</td>
                                            <td>{ticket.start_range && ticket.end_range ? `${formatDate(ticket.start_range)} To ${formatDate(ticket.end_range)}` : 'N/A'}</td>
                                            <td>{formatDeno(100, ticket.deno_100)}</td>
                                            <td>{formatDeno(50, ticket.deno_50)}</td>
                                            <td>{formatDeno(20, ticket.deno_20)}</td>
                                            <td>{formatDeno(10, ticket.deno_10)}</td>
                                            <td>{formatDeno(5, ticket.deno_5)}</td>
                                            <td>{formatDeno(2, ticket.deno_2)}</td>
                                            <td>{formatDeno(1, ticket.deno_1)}</td>
                                            <td>{`$${currencyTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center">
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                    variant="secondary"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1 || depositTickets.length === 0}
                >
                    Previous
                </Button>
                <span>
                    Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <Button
                    variant="secondary"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages || depositTickets.length === 0}
                >
                    Next
                </Button>
            </div>
        </Container>
    );
};

export default DepositTicketsList;